import React, { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';
import { IconName, Illustration } from '@klp/design/Illustrations';
import { IconButton, Grid } from '@material-ui/core';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { subYears, isAfter, isSameYear, format, isValid } from 'date-fns';
import { Block, Heading, Tooltip } from '@klp/design';
import { KlpFontVariant } from '@klp/design/Theme/KlpFontVariant';
import { Kommentarer, NyKommentar } from '../components';
import {
    kommentarerSelectorActions,
    kommentarerSelectors,
} from '../store/kommentarerSlice';
import { useTranslation } from 'react-i18next';
import { useLazyGetKommentarerQuery } from '../services/rtkApi';
import { contextSelectors } from '@klp/contextSelector';
import { kommentarerAccess } from '../config';

export const defaultDrawerWidth = 500;
const minDrawerWidth = 50;
const maxDrawerWidth = 1000;

const useStyles = () =>
    makeStyles((theme) => ({
        tooltip: {
            position: 'absolute',
            right: '10px',
            top: theme.header.height + theme.spacing(1),
        },
        drawer: {
            flexShrink: 0,
        },
        toolbar: theme.mixins.toolbar,
        dragger: {
            width: '5px',
            cursor: 'ew-resize',
            padding: '4px 0 0',
            borderTop: '1px solid #ddd',
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            zIndex: 100,
            backgroundColor: '#f4f7f9',
        },
        ikon: {
            filter: 'invert(100%) sepia(100%) saturate(0%) hue-rotate(132deg) brightness(103%) contrast(103%)',
        },
        button: {
            backgroundColor: theme.palette.secondary?.dark,
            '&:hover': {
                backgroundColor: theme.palette.secondary?.main,
            },
        },
        pin: {
            '& .MuiIcon-root': {
                fontSize: KlpFontVariant.default.fontSize,
            },
        },
        datePicker: {
            width: '150px',
        },
    }));

const routes = {
    '/': '/',
    '/contractmanagement/dynamicnegotation': 'contractManagement.title',
    '/contractmanagement/registration': 'contractManagement.title',
    '/project/report': 'breadcrumbs.project.report',
    '/report': 'report.title',
    '/environment': 'environment.title',
    '/areakeyfigures': 'keyfigures.areaTitle',
    '/economykeyfigures': 'keyfigures.economyTitle',
    '/baerekraft/miljo': 'environment.miljoprofil',
    '/baerekraft/environment': 'environment.powerBi',
};

export default function KommentarerView({ disableDragger = false }) {
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const classes = useStyles()();
    const [open, setOpen] = useState(false);
    const [drawerWidth, setDrawerWidth] = useState(defaultDrawerWidth);
    const { pinKommentarerMenu, selectedDate } =
        useSelector(kommentarerSelectors);
    const { selectedContext } = useSelector(contextSelectors);
    const dispatch = useDispatch();

    const {
        savePinCommentMenuToStorage,
        clearHighlightedCommentsToRemoveFromStorage,
        saveSelectedDateToStorage,
        reducerActions: { setEditMode, setPostMode },
    } = kommentarerSelectorActions;

    const handleDateChange = (date: Date) => {
        if (!isValid(date)) {
            return;
        }
        dispatch(saveSelectedDateToStorage(date));
    };

    const handleDrawerOpen = () => {
        setOpen(true);
    };

    const handleDrawerClose = () => {
        setOpen(false);
        dispatch(savePinCommentMenuToStorage(false));
        dispatch(setEditMode({ editFlag: false, kommentarId: '' }));
        dispatch(setPostMode(false));
    };

    const handleMouseDown = (
        _e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    ) => {
        document.addEventListener('mouseup', handleMouseUp, true);
        document.addEventListener('mousemove', handleMouseMove, true);
    };

    const handleMouseUp = () => {
        document.removeEventListener('mouseup', handleMouseUp, true);
        document.removeEventListener('mousemove', handleMouseMove, true);
    };

    const handleMouseMove = useCallback((e) => {
        const offsetRight =
            document.body.offsetWidth - (e.clientX - document.body.offsetLeft);
        if (offsetRight > minDrawerWidth && offsetRight < maxDrawerWidth) {
            setDrawerWidth(offsetRight);
        }
    }, []);

    const handleCommentMenuPin = () => {
        dispatch(savePinCommentMenuToStorage(!pinKommentarerMenu));
    };

    const handleClearHighlightedComments = () => {
        dispatch(clearHighlightedCommentsToRemoveFromStorage());
    };

    const handleClose = () => {
        dispatch(setEditMode({ editFlag: false, kommentarId: '' }));
    };

    const [getKommentarer] = useLazyGetKommentarerQuery();

    useEffect(() => {
        if (selectedContext?.id) {
            getKommentarer({
                byggNummer: selectedContext?.id,
                kunfremhevde: false,
                kategori: pathname.substring(1),
                gjelderForAar: format(selectedDate, 'yyyy'),
            }).then((response) => {
                const comments = response.data ?? [];

                const oneYearAgo = subYears(new Date(), 1);
                const commentsFromThisYearOrLast = comments.filter(
                    (comment) => {
                        if (!comment.created) return false;
                        const commentDate = new Date(comment.created);
                        if (!isValid(commentDate)) return false;
                        return (
                            isAfter(commentDate, oneYearAgo) ||
                            isSameYear(commentDate, new Date())
                        );
                    },
                );

                if (commentsFromThisYearOrLast.length > 0 && pathname !== '/') {
                    setOpen(true);
                    dispatch(savePinCommentMenuToStorage(true));
                }
            });
        }
    }, [selectedContext, selectedDate, pathname, getKommentarer]);

    useEffect(() => {
        return () => {
            dispatch(savePinCommentMenuToStorage(false)); // Unpin the menu when the component is unmounted
        };
    }, [pathname]);

    if (!selectedContext || !kommentarerAccess()) {
        return null;
    }

    return (
        <>
            {!open && !pinKommentarerMenu ? (
                <div className={classes.tooltip}>
                    <Tooltip translation="comments.tooltip" title="">
                        <IconButton
                            onClick={handleDrawerOpen}
                            className={classes.button}
                        >
                            <Illustration
                                className={classes.ikon}
                                icon={IconName.EDIT}
                                klpPalette="primary"
                                klpColor="main"
                                size="medium"
                            />
                        </IconButton>
                    </Tooltip>
                </div>
            ) : (
                <Drawer
                    className={classes.drawer}
                    anchor="right"
                    variant="persistent"
                    open={open || pinKommentarerMenu}
                    PaperProps={{ style: { width: drawerWidth } }}
                >
                    <div className={classes.toolbar} />
                    {!disableDragger ? (
                        <>
                            <div
                                onMouseDown={(e) => handleMouseDown(e)}
                                className={classes.dragger}
                            />
                        </>
                    ) : null}
                    <Grid
                        container={true}
                        alignItems="center"
                        justifyContent="space-between"
                        direction="row"
                    >
                        <Grid item={true}>
                            <Block paddingLeft="bulky" paddingBottom="compact">
                                <Tooltip translation="comments.pin" title="">
                                    <IconButton
                                        className={classes.pin}
                                        onClick={handleCommentMenuPin}
                                        color={
                                            pinKommentarerMenu
                                                ? 'secondary'
                                                : 'primary'
                                        }
                                    >
                                        <Illustration
                                            icon={
                                                pinKommentarerMenu
                                                    ? IconName.RIGHT
                                                    : IconName.LEFT
                                            }
                                        />
                                    </IconButton>
                                </Tooltip>
                            </Block>
                        </Grid>
                        <Grid item={true}>
                            <Block paddingLeft="bulky" paddingBottom="compact">
                                <Tooltip
                                    translation="comments.resetHighlightsFrontpage"
                                    title=""
                                >
                                    <IconButton
                                        onClick={handleClearHighlightedComments}
                                    >
                                        <Illustration
                                            icon={IconName.GEAR}
                                            klpPalette="primary"
                                            klpColor="main"
                                        />
                                    </IconButton>
                                </Tooltip>{' '}
                                <Tooltip translation="comments.close" title="">
                                    <IconButton onClick={handleDrawerClose}>
                                        <Illustration
                                            icon={IconName.CLOSE}
                                            klpPalette="primary"
                                            klpColor="main"
                                        />
                                    </IconButton>
                                </Tooltip>
                            </Block>
                        </Grid>
                    </Grid>
                    <Block paddingLeft="fat">
                        <Grid
                            container={true}
                            alignItems="center"
                            justifyContent="space-between"
                            direction="row"
                        >
                            <Grid item={true}>
                                <Heading
                                    klpHeadingLevel="TWO"
                                    translation="comments.title"
                                    data-cy="CommentsView__Title"
                                    translationOptions={{
                                        page:
                                            routes[pathname] !== '/'
                                                ? t(`${routes[pathname]}`)
                                                : ' ',
                                    }}
                                />
                            </Grid>
                        </Grid>
                        <Block
                            className={classes.datePicker}
                            paddingTop="compact"
                        >
                            <KeyboardDatePicker
                                fullWidth={true}
                                allowKeyboardControl={true}
                                views={['year']}
                                label={t('comments.datePickerLabel')}
                                variant="inline"
                                inputVariant="outlined"
                                InputAdornmentProps={{
                                    position: 'end',
                                }}
                                value={selectedDate}
                                onChange={(e) =>
                                    handleDateChange(e as unknown as Date)
                                }
                                maxDate={new Date()}
                                autoOk={true}
                            />
                        </Block>
                        <NyKommentar
                            onCancel={handleClose}
                            onSave={handleClose}
                        />
                        <Kommentarer />
                    </Block>
                </Drawer>
            )}
        </>
    );
}
